import isEmpty from 'lodash/isEmpty'
import { useCallback, useContext } from 'react'

import { CheckoutFlowModel, CheckoutFlowStep } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { CheckoutCartV2, CheckoutCartV2Address, CheckoutV2AddressMeta, CheckoutV2PaymentMethod } from '@Pages/onestepcheckout'
import { AddressValidationV2 } from '@Shared/Components/Forms/Validations/AddressValidation'
import { CitySingaporeValidationV2 } from '@Shared/Components/Forms/Validations/CitySingaporeValidation'
import { EmailValidationV2 } from '@Shared/Components/Forms/Validations/EmailValidation'
import { getAddressMaxLength, MaxLengthValidationV2 } from '@Shared/Components/Forms/Validations/MaxLengthValidation'
import { NotEmptyValidationV2, StateNotEmptyValidationV2 } from '@Shared/Components/Forms/Validations/NotEmptyValidation'
import { PhoneLengthValidationV2 } from '@Shared/Components/Forms/Validations/PhoneLengthValidation'
import { ZipCodeValidationV2 } from '@Shared/Components/Forms/Validations/ZipCodeValidation'
import { TextInputValidationFn } from '@Shared/Components/Inputs/TextInput/TextInput'

export const FREE_PAYMENT = {
  type: 'freePayment',
  id: 'isFreePayment',
} as CheckoutV2PaymentMethod
export const COD_PAYMENT = {
  type: 'cod',
  id: 'cod',
} as CheckoutV2PaymentMethod

export const useCheckoutFieldsValidations = () => {
  const { t } = useTranslation()
  const { store } = useContext(MetadataContext)
  const getValidations = useCallback((countryCode: string | null | undefined) => ({
    email: [NotEmptyValidationV2, EmailValidationV2],
    streetName: [
      NotEmptyValidationV2,
      MaxLengthValidationV2(getAddressMaxLength(store.id), t),
      AddressValidationV2(store.id, t),
    ],
    additionalStreetInfo: [MaxLengthValidationV2(getAddressMaxLength(store.id), t)],
    city: [NotEmptyValidationV2, CitySingaporeValidationV2(store.id, t)],
    company: [],
    externalCustomerID: [],
    externalID: [],
    country: [NotEmptyValidationV2],
    firstName: [NotEmptyValidationV2],
    lastName: [NotEmptyValidationV2],
    phone: [
      NotEmptyValidationV2,
      PhoneLengthValidationV2(t, 6, store.id === '17' ? 9 : undefined),
    ],
    phonePrefix: [NotEmptyValidationV2],
    state: [StateNotEmptyValidationV2(t)],
    region: [StateNotEmptyValidationV2(t)],
    postalCode: [ZipCodeValidationV2(countryCode || store.config?.countryCode, t), NotEmptyValidationV2],
  }), [store.config?.countryCode, store.id, t])
  return { getValidations }
}

const checkValidations = (
  validationRecord: (countryCode: string | null | undefined) => Record<Partial<keyof CheckoutCartV2Address>, TextInputValidationFn[]>,
  model: CheckoutCartV2Address,
  keys: Array<keyof CheckoutCartV2Address>,
) => keys.every(key => validationRecord(model.country)[key].every(fn => fn(`${model[key]}`).success))

export const useCheckoutFlowStepValidation = (
  config: CheckoutV2AddressMeta | null,
  validations: Record<CheckoutFlowStep, boolean>,
  cart: CheckoutCartV2,
) => {
  const { getValidations } = useCheckoutFieldsValidations()

  // ADDRESS STEP
  const validateAddressStep = useCallback(
    (model: CheckoutFlowModel) => {
      const billingEntriesToCheck: Array<keyof CheckoutCartV2Address>
        = ['email', 'firstName', 'lastName', 'postalCode', 'streetName', 'city', 'phone', 'country', 'phonePrefix']
      const shippingEntriesToCheck: Array<keyof CheckoutCartV2Address>
        = ['firstName', 'lastName', 'postalCode', 'streetName', 'city', 'country']
      if (config && config.showAdditionalStreetInfo) {
        billingEntriesToCheck.push('additionalStreetInfo')
        shippingEntriesToCheck.push('additionalStreetInfo')
      }
      if (config && config.showState) {
        billingEntriesToCheck.push('state')
        shippingEntriesToCheck.push('state')
      }
      if (config && config.showRegion) {
        billingEntriesToCheck.push('region')
        shippingEntriesToCheck.push('region')
      }
      const billingAddressVerification = !!cart.billingAddress
        && checkValidations(getValidations, cart.billingAddress, billingEntriesToCheck)
        && checkValidations(getValidations, model.billingAddress, billingEntriesToCheck)
      if (model.showShippingAddress.checked) {
        const shippingAddressVerification = !!cart.shippingAddress
          && checkValidations(getValidations, cart.shippingAddress, shippingEntriesToCheck)
          && checkValidations(getValidations, model.shippingAddress, shippingEntriesToCheck)
        return billingAddressVerification && shippingAddressVerification
      }
      return billingAddressVerification
    },
    [cart.billingAddress, cart.shippingAddress, config, getValidations],
  )

  // DELIVERY STEP
  const validateDeliveryStep = useCallback(() => {
    if (!validations.ADDRESS) {
      return false
    }
    const shipping = cart.availableShippingMethods?.find(a => a.id === cart.shippingMethodID)
    if (!shipping) {
      return false
    }
    const isDropPointDelivery = shipping.isServiceLocation
    if (isDropPointDelivery) {
      const servicelocation = shipping.serviceLocations?.find(
        s => !!cart.deliveryAddress?.externalID && s.externalID && cart.deliveryAddress?.externalID === s.externalID,
      )
      const isValid = !!servicelocation
      return !!shipping && !!isValid
    }
    return !!shipping
  }, [cart.availableShippingMethods, cart.deliveryAddress?.externalID, cart.shippingMethodID, validations.ADDRESS])

  // PAYMENT STEP
  const validatePaymentStep = useCallback((model: CheckoutFlowModel) => {
    if (!validations.ADDRESS) {
      return false
    }
    const isFree = model?.paymentStep?.payment?.id === FREE_PAYMENT.id
    const isTermsAccepted = model?.paymentStep?.terms === true
    if (isFree) {
      return isTermsAccepted && model?.paymentStep?.payment?.id === FREE_PAYMENT.id
    }
    const isCOD = !!cart.availableShippingMethods?.find(a => a.id === cart.shippingMethodID)?.isCOD
    if (isCOD) {
      return isTermsAccepted && isCOD
    }
    return isTermsAccepted && !isEmpty(model?.paymentStep?.payment?.id)
  }, [cart.availableShippingMethods, cart.shippingMethodID, validations.ADDRESS])

  return {
    validateAddressStep,
    validateDeliveryStep,
    validatePaymentStep,
  }
}
